/**
 * DeviceGridCard — card visual pra 1 device. Usado no painel direito do
 * split panel do Admin → Devices.
 *
 * Densidade média: mostra nome, role, local_id, status online/bateria/sinal
 * e badges (AC/Bateria/Inativo). Ações (Editar, Detalhes, Deletar) em ícones
 * pequenos no rodapé.
 */

import { Cpu, Pencil, Trash2 } from "lucide-react";
import clsx from "clsx";

import type { Device, DeviceStatus } from "@/types";
import { roleLabel } from "@/lib/format";

import { Badge } from "./Badge";
import { BatteryIndicator, OnlineIndicator, SignalIndicator } from "./HealthIndicators";
import { Button } from "./Button";

interface Props {
  device: Device;
  status: DeviceStatus | null;
  /**
   * O ativo à qual o device está vinculado foi arquivada. O device segue
   * `active`, mas o engine de alertas não avalia equipment arquivado — então
   * ele está de fato dormente. Sem esse aviso o card induz ao erro.
   */
  equipmentArchived?: boolean;
  onDetails?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function DeviceGridCard({
  device, status, equipmentArchived, onDetails, onEdit, onDelete,
}: Props) {
  const inactive = !device.active;
  // Device ativo num ativo arquivado: não gera alerta, mas não está inativo.
  const dormant = !inactive && !!equipmentArchived;

  return (
    <div
      className={clsx(
        "flex flex-col gap-3 rounded-lg border p-4 transition-shadow",
        "border-surface-200 dark:border-slate-800",
        "bg-white dark:bg-slate-900 hover:shadow-card-hover",
        (inactive || dormant) && "opacity-60",
      )}
    >
      {/* Header — nome + role + power_source */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 flex-none text-slate-400 dark:text-slate-500" />
            <span
              className={clsx(
                "truncate text-sm font-medium",
                inactive
                  ? "text-slate-500 dark:text-slate-500 line-through"
                  : "text-slate-900 dark:text-slate-100",
              )}
            >
              {device.name}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-xs">
            <Badge tone="brand">{roleLabel(device.role)}</Badge>
            {device.power_source === "ac" && (
              <span title="Rede elétrica" className="text-amber-600 dark:text-amber-400">⚡</span>
            )}
            {device.power_source === "battery" && (
              <span title="Bateria" className="text-emerald-600 dark:text-emerald-400">🔋</span>
            )}
          </div>
        </div>
        {inactive && <Badge tone="neutral">Inativo</Badge>}
        {dormant && (
          <span title="O ativo deste sensor está arquivado — sem alertas">
            <Badge tone="warning">Ativo arquivado</Badge>
          </span>
        )}
      </div>

      {/* local_id monospace */}
      <div className="text-xs text-slate-500 dark:text-slate-400">
        <code className="rounded bg-surface-100 dark:bg-slate-800 px-1.5 py-0.5">
          {device.local_id}
        </code>
      </div>

      {/* Saúde — 3 indicadores lado a lado */}
      <div className="flex flex-wrap items-center gap-3 border-t border-surface-100 dark:border-slate-800 pt-3">
        <OnlineIndicator online={status?.online} />
        {status?.battery_pct != null && (
          <BatteryIndicator pct={status.battery_pct} size="sm" />
        )}
        {(status?.signal_dbm != null || status?.signal_lqi != null) && (
          <SignalIndicator
            dbm={status?.signal_dbm ?? undefined}
            lqi={status?.signal_lqi ?? undefined}
            size="sm"
          />
        )}
      </div>

      {/* Ações */}
      <div className="mt-auto flex items-center justify-end gap-1">
        {onDetails && (
          <Button size="sm" variant="ghost" onClick={onDetails}>
            Detalhes
          </Button>
        )}
        {onEdit && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil className="h-3.5 w-3.5" />}
            onClick={onEdit}
          >
            Editar
          </Button>
        )}
        {onDelete && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
}
